import clsx from 'clsx';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { handleEscKeyDown } from '../../../../Accessibility/KeyboardEvents';
import { T, translation, TranslationsContext } from '../../../../Translations';
import CloseIcon from '../../../Cutesies/CloseIcon';

interface Props {
  close: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  customBackgroundCssClasses?: string;
  modalWidth?: string;
}

class Modal extends React.Component<Props> {
  portal: HTMLElement;

  constructor(props: Props) {
    super(props);
    this.portal = document.createElement('div');
    this.portal.id = `${this.props.title} portal`;
    const root = document.getElementById('root');
    if (root) {
      root.appendChild(this.portal);
      document.body.classList.add('overflow-hidden');
    }
  }

  componentWillUnmount() {
    const root = document.getElementById('root');
    if (root && this.portal) {
      root.removeChild(this.portal);
      document.body.classList.remove('overflow-hidden');
    }
  }

  modalWidth = () =>
    this.props.modalWidth ? this.props.modalWidth : 'w-auto max-w-[600px] overflow-hidden';

  render() {
    const { close, title, children, customBackgroundCssClasses } = this.props;
    return ReactDOM.createPortal(
      <TranslationsContext.Consumer>
        {(ts) => (
          <FocusTrap
            focusTrapOptions={{ escapeDeactivates: false, fallbackFocus: '#modal-container' }}
          >
            <div
              className="fixed inset-0 z-20 flex items-center justify-center text-[#212121]"
              data-test-modal={true}
              aria-modal={true}
              onKeyDown={handleEscKeyDown(close)}
              id="modal-container"
              tabIndex={-1}
            >
              <div
                className={clsx(
                  `${this.modalWidth()} rounded-md bg-white`,
                  'relative z-[1] my-0 mx-5 lg:m-0 lg:w-4/5',
                  'overflow-hidden flex flex-col',
                )}
                data-test-modal-body={true}
              >
                <div className="sticky top-0 bg-white z-10">
                  <div className="flex flex-row flex-wrap justify-between border-b-2">
                    <div className="flex-1 md:flex-1/2">{title}</div>
                    <button
                      type="button"
                      aria-label={translation('Back to your Learning Collection')(ts)}
                      className={clsx(
                        'hidden md:inline-flex',
                        'rounded-md border-1 border-teal-600 bg-white',
                        'h-10 mr-10 mt-10 p-2',
                        'text-sm text-teal-600 font-semibold',
                        'hover:opacity-70 cursor-pointer',
                      )}
                      onClick={close}
                      data-test-close-button={true}
                      data-test-button="Back to your Learning Collection"
                      data-intercom-target="back-to-learning-collection-button"
                    >
                      <T kind="Back to your Learning Collection" />
                    </button>
                    <div className="inline-flex mr-4 mt-4 md:hidden" onClick={close}>
                      <CloseIcon />
                    </div>
                  </div>
                </div>
                <div
                  className={clsx(
                    'overflow-auto flex-grow',
                    'mt-8 md:mt-1 w-full pb-6 font-normal text-black',
                  )}
                  data-test-modal-content={true}
                >
                  {children}
                </div>
              </div>
              <div
                className={clsx(
                  'absolute inset-0 bg-[#1f2229] opacity-75',
                  customBackgroundCssClasses,
                )}
                data-test-modal-shade={true}
                onClick={close}
              />
            </div>
          </FocusTrap>
        )}
      </TranslationsContext.Consumer>,
      this.portal,
    );
  }
}

export default Modal;
